import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '@/styled-system/css';
import { useDetailPanel } from './DetailPanelContext';
import { DetailPanelHeader } from './DetailPanelHeader';
import * as styles from './detailPanel.styles';

type DetailPanelOutletProps = {
  headerActions?: ReactNode;
};

export const DetailPanelOutlet = ({ headerActions }: DetailPanelOutletProps) => {
  const { activeContent: Content, activeProvider: Provider, panelConfig, closePanel, closeLabel } = useDetailPanel();
  const isOpen = Content !== null;

  const outletRef = useRef<HTMLDivElement>(null);
  const [summaryHidden, setSummaryHidden] = useState(false);

  const config = panelConfig as { subtitle?: string } | undefined;
  const subtitle = config?.subtitle;

  const observerRef = useRef<IntersectionObserver | null>(null);

  const setupObserver = useCallback(() => {
    observerRef.current?.disconnect();
    setSummaryHidden(false);

    if (!subtitle || !outletRef.current) return;

    const sentinel = outletRef.current.querySelector('[data-panel-summary-sentinel]');
    if (!sentinel) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setSummaryHidden(!entry.isIntersecting);
      },
      { root: outletRef.current, threshold: 0 },
    );
    observerRef.current.observe(sentinel);
  }, [subtitle]);

  useEffect(() => {
    setupObserver();
    return () => observerRef.current?.disconnect();
  }, [setupObserver, Content]);

  const renderContent = () => {
    if (!Content) return null;

    const body = <Content config={panelConfig} />;

    if (Provider) {
      return <Provider>{body}</Provider>;
    }

    return body;
  };

  return (
    <div
      ref={outletRef}
      className={cx(styles.panelOutlet, isOpen ? styles.panelOutletVisible : styles.panelOutletHidden)}
      aria-hidden={!isOpen}
      data-testid="detail-panel-outlet"
    >
      <DetailPanelHeader
        onClose={closePanel}
        actions={headerActions}
        closeLabel={closeLabel}
        subtitle={summaryHidden ? subtitle : undefined}
      />
      <div className={styles.panelBody}>{renderContent()}</div>
    </div>
  );
};

export type { DetailPanelOutletProps };
